import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { clinicalParametersAPI } from '../services/api';
import { setDbParameterMap } from '../utils/vitalSignsValidation';

const ClinicalParametersContext = createContext(null);

let _cachedParameters = null;
let _cachePromise = null;

export function ClinicalParametersProvider({ children }) {
  const [parameters, setParameters] = useState(_cachedParameters || []);
  const [loading, setLoading] = useState(!_cachedParameters);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  const fetchParameters = useCallback(async (force = false) => {
    if (!force && _cachedParameters) {
      setParameters(_cachedParameters);
      setLoading(false);
      return;
    }
    if (!force && _cachePromise) {
      const data = await _cachePromise;
      setParameters(data);
      setLoading(false);
      return;
    }
    // Skip the fetch entirely on public routes — the clinical parameters
    // endpoint requires authentication and calling it without a token
    // produces a 401 on every public page load.
    const token = localStorage.getItem('token');
    if (!token && !force) {
      setLoading(false);
      return [];
    }
    setLoading(true);
    setError(null);
    _cachePromise = clinicalParametersAPI.list()
      .then(res => {
        const data = res.data || res;
        _cachedParameters = data;
        fetchedRef.current = true;
        setParameters(data);
        setLoading(false);
        return data;
      })
      .catch(err => {
        // Never cache a failed fetch: reset the cache promise so the next
        // call retries instead of returning an empty list for the session.
        _cachePromise = null;
        setError(err.message || 'Failed to load clinical parameters');
        setLoading(false);
        return [];
      });
    await _cachePromise;
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchParameters();
    }
  }, [fetchParameters]);

  // Build a lookup map by parameter_key
  const paramMap = React.useMemo(() => {
    const m = {};
    for (const p of parameters) {
      m[p.parameter_key] = p;
    }
    return m;
  }, [parameters]);

  // Sync DB parameter map to the vitalSignsValidation utility module
  // so non-React callers (e.g. validateVitalSign) can use DB-backed ranges.
  useEffect(() => {
    setDbParameterMap(paramMap);
  }, [paramMap]);

  // Validate a single value against DB-backed ranges (with fallback to no-op)
  const validateParameter = useCallback((key, value, ageYears = null, gender = null) => {
    if (value === null || value === undefined || value === '') {
      return { status: 'neutral', color: 'gray', message: '' };
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return { status: 'neutral', color: 'gray', message: '' };
    }

    const param = paramMap[key];
    if (!param || !param.is_active) {
      return { status: 'neutral', color: 'gray', message: '' };
    }

    // Resolve age-specific ranges
    let ranges = {
      normal_min: param.normal_min,
      normal_max: param.normal_max,
      critical_low: param.critical_low,
      critical_high: param.critical_high,
    };

    if (param.age_specific_ranges && ageYears !== null && ageYears !== undefined) {
      const age = Number(ageYears);
      if (!isNaN(age)) {
        for (const bracket of param.age_specific_ranges) {
          if (bracket.age_min !== undefined && bracket.age_max !== undefined && bracket.age_min <= age && age < bracket.age_max) {
            for (const k of Object.keys(ranges)) {
              if (bracket[k] !== undefined && bracket[k] !== null) {
                ranges[k] = bracket[k];
              }
            }
            break;
          }
        }
      }
    }

    const { normal_min, normal_max, critical_low, critical_high } = ranges;
    const unit = param.unit || '';

    if (normal_min !== null && normal_max !== null && numValue >= normal_min && numValue <= normal_max) {
      return { status: 'normal', color: 'green', message: `Normal (${normal_min}-${normal_max} ${unit})`.trim() };
    }

    if (critical_low !== null && numValue <= critical_low) {
      return { status: 'critical', color: 'red', message: `Critical low (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
    }
    if (critical_high !== null && numValue >= critical_high) {
      return { status: 'critical', color: 'red', message: `Critical high (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
    }

    if (normal_min !== null && numValue < normal_min) {
      return { status: 'abnormal', color: 'orange', message: `Low (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
    }
    if (normal_max !== null && numValue > normal_max) {
      return { status: 'abnormal', color: 'orange', message: `High (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
    }

    return { status: 'neutral', color: 'gray', message: '' };
  }, [paramMap]);

  // Get the effective range for a parameter (age-aware)
  const getRange = useCallback((key, ageYears = null, gender = null) => {
    const param = paramMap[key];
    if (!param) return null;

    let ranges = {
      normal_min: param.normal_min,
      normal_max: param.normal_max,
      critical_low: param.critical_low,
      critical_high: param.critical_high,
    };

    if (param.age_specific_ranges && ageYears !== null && ageYears !== undefined) {
      const age = Number(ageYears);
      if (!isNaN(age)) {
        for (const bracket of param.age_specific_ranges) {
          if (bracket.age_min !== undefined && bracket.age_max !== undefined && bracket.age_min <= age && age < bracket.age_max) {
            for (const k of Object.keys(ranges)) {
              if (bracket[k] !== undefined && bracket[k] !== null) {
                ranges[k] = bracket[k];
              }
            }
            break;
          }
        }
      }
    }

    return { ...ranges, unit: param.unit, name: param.display_name };
  }, [paramMap]);

  // Compute derived parameters from input values
  const computeParameter = useCallback((key, inputs, ageYears = null, gender = null) => {
    const param = paramMap[key];
    if (!param || !param.formula) return null;

    const safeVars = {};
    for (const inputKey of (param.formula_inputs || [])) {
      const raw = inputs[inputKey];
      if (raw === null || raw === undefined || raw === '') return null;
      const num = parseFloat(raw);
      if (isNaN(num)) return null;
      safeVars[inputKey] = num;
    }

    try {
      // Use a sandboxed Function constructor instead of eval() to prevent
      // arbitrary code execution from DB-stored clinical formulas.
      // Only the explicitly-passed variable names are accessible inside the
      // function body — globals like window, document, fetch, localStorage
      // are shadowed by the parameter scope.
      const varNames = Object.keys(safeVars);
      const varValues = Object.values(safeVars);
      const sandboxedFn = new Function(...varNames, `"use strict"; return (${param.formula});`);
      const computed = sandboxedFn(...varValues);
      const computedValue = Math.round(parseFloat(computed) * 100) / 100;
      const validation = validateParameter(key, computedValue, ageYears, gender);
      return {
        parameter_key: key,
        display_name: param.display_name,
        computed_value: computedValue,
        unit: param.unit || '',
        severity: validation.status,
        message: validation.message,
        reference_range: validation.message,
      };
    } catch {
      return null;
    }
  }, [paramMap, validateParameter]);

  // Compute all applicable computed parameters from a set of inputs
  const computeAll = useCallback((inputs, ageYears = null, gender = null) => {
    const results = [];
    for (const param of parameters) {
      if (param.category !== 'computed' || !param.formula || !param.is_active) continue;
      const required = param.formula_inputs || [];
      if (!required.every(k => inputs[k] !== null && inputs[k] !== undefined && inputs[k] !== '')) continue;

      const result = computeParameter(param.parameter_key, inputs, ageYears, gender);
      if (result) results.push(result);
    }
    return results;
  }, [parameters, computeParameter]);

  const value = {
    parameters,
    paramMap,
    loading,
    error,
    fetchParameters,
    validateParameter,
    getRange,
    computeParameter,
    computeAll,
  };

  return (
    <ClinicalParametersContext.Provider value={value}>
      {children}
    </ClinicalParametersContext.Provider>
  );
}

export function useClinicalParameters() {
  const ctx = useContext(ClinicalParametersContext);
  if (!ctx) {
    // Return a safe fallback so components work even without the provider
    return {
      parameters: [],
      paramMap: {},
      loading: false,
      error: null,
      fetchParameters: () => {},
      validateParameter: () => ({ status: 'neutral', color: 'gray', message: '' }),
      getRange: () => null,
      computeParameter: () => null,
      computeAll: () => [],
    };
  }
  return ctx;
}
